import { sendAnalytics } from '@audio-network/analytics'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

import { DOWNLOAD_CENTRE_POPOVER_ID } from '@/components/layout/Header/DownloadCentre/DownloadCentre.constants'
import { DOWNLOAD_COMPLETE_MESSAGE_VISIBLE_MS } from '@/components/layout/Header/DownloadCentre/DownloadCentre.constants'
import { WorkingArea } from '@/constants/user/userWorkingArea'
import { getCurrentCart } from '@/services/cartService'
import { hasFailedDownloadCentreMessage } from '@/stores/downloadCentreStore'
import { hasInFlightDownloadCentreMessage } from '@/stores/downloadCentreStore'
import useDownloadCentreStore from '@/stores/downloadCentreStore'
import matchByLocale from '@/utils/matchByLocale'

import {
	UserHeaderActions,
	UserHeaderActionsProps,
} from './HeaderActions.types'
import { getDownloadCentreAnalyticsStatus } from './HeaderActions.utils'

const isDownloadCentreOpen = (): boolean => {
	const popover = document.getElementById(DOWNLOAD_CENTRE_POPOVER_ID)
	if (!popover) return false

	try {
		if (popover.matches(':popover-open')) return true
	} catch {
		// jsdom and some engines do not support :popover-open
	}

	return popover.dataset.showPopover === 'true'
}

export const useShowDownloadRoundelSuccess = (): boolean => {
	const isDownloading = useDownloadCentreStore((state) =>
		hasInFlightDownloadCentreMessage(state.messages)
	)
	const hasDownloadError = useDownloadCentreStore((state) =>
		hasFailedDownloadCentreMessage(state.messages)
	)
	const completedMessageIds = useDownloadCentreStore((state) =>
		state.messages
			.filter((message) => message.status === 'completed')
			.map((message) => message.id)
			.join(',')
	)
	const [showSuccessHighlight, setShowSuccessHighlight] = useState(false)

	useEffect(() => {
		if (hasDownloadError) {
			setShowSuccessHighlight(false)
			return
		}

		if (isDownloading) {
			setShowSuccessHighlight(true)
			return
		}

		if (!completedMessageIds) {
			setShowSuccessHighlight(false)
			return
		}

		setShowSuccessHighlight(true)

		const timeoutId = window.setTimeout(() => {
			setShowSuccessHighlight(false)
		}, DOWNLOAD_COMPLETE_MESSAGE_VISIBLE_MS)

		return () => {
			window.clearTimeout(timeoutId)
		}
	}, [completedMessageIds, hasDownloadError, isDownloading])

	return showSuccessHighlight
}

export const useHeaderActions = ({
	user,
}: UserHeaderActions): UserHeaderActionsProps => {
	const { locale } = useRouter()
	const [itemsCount, setItemsCount] = useState<number | null>(null)
	const downloadCentreWasClosedOnActivate = useRef(false)

	useEffect(() => {
		if (!user || typeof document === 'undefined') {
			setItemsCount(null)
			return
		}

		const fetchCartCount = (): void => {
			getCurrentCart().then((cart) => {
				setItemsCount(cart?.itemsCount ?? 0)
			})
		}

		if (document.visibilityState === 'visible') {
			fetchCartCount()
		}

		const handleVisibilityChange = (): void => {
			if (document.visibilityState === 'visible') {
				fetchCartCount()
			}
		}

		const handlePageShow = (event: PageTransitionEvent): void => {
			if (event.persisted) {
				fetchCartCount()
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)
		window.addEventListener('pageshow', handlePageShow)
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			window.removeEventListener('pageshow', handlePageShow)
		}
	}, [user])

	// all users on the CN site with the CNServiceMenu toggle on should see the CN service menu
	const showCNServiceMenu =
		user &&
		matchByLocale(locale, {
			'zh-CN': true,
		})

	const showServiceMenu = user && !showCNServiceMenu

	const hasTargetWorkingArea =
		user?.workingArea === WorkingArea['TV & Film'] ||
		user?.workingArea === WorkingArea.Advertising ||
		user?.workingArea === WorkingArea['Brands & Business']

	const newTargetUser = !user?.organisation?.serviceTier && hasTargetWorkingArea

	const accountManagedTargetUser =
		user?.organisation?.serviceTier === 'Account Managed' ||
		user?.organisation?.serviceTier === 'VIP'

	const showAddToTeamMenu = newTargetUser || accountManagedTargetUser

	const handleAddToTeamButtonClick = (): void => {
		sendAnalytics({
			noun: 'Add to Team',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Location: 'Music App Navigation',
			},
		})
	}

	const handleCartButtonClick = (): void => {
		sendAnalytics({
			noun: 'Cart',
			pastTenseVerb: 'Clicked',
			eventParameters: { Location: 'Music App Navigation' },
		})
	}

	const handleServiceMenuButtonClick = (): void => {
		sendAnalytics({
			noun: 'Service Menu',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Location: 'Music App Navigation',
			},
		})
	}

	const handleAccountMenuButtonClick = (): void => {
		sendAnalytics({
			noun: 'Account Menu',
			pastTenseVerb: 'Clicked',
			eventParameters: { Location: 'Music App Navigation' },
		})
	}

	const captureDownloadCentreClosedState = (): void => {
		downloadCentreWasClosedOnActivate.current = !isDownloadCentreOpen()
	}

	const handleDownloadCentreButtonPointerDown = (): void => {
		captureDownloadCentreClosedState()
	}

	const handleDownloadCentreButtonKeyDown = (
		event: React.KeyboardEvent<HTMLButtonElement>
	): void => {
		if (event.key !== 'Enter' && event.key !== ' ') return
		captureDownloadCentreClosedState()
	}

	const handleDownloadCentreButtonClick = (): void => {
		if (!downloadCentreWasClosedOnActivate.current) return

		sendAnalytics({
			noun: 'Download Centre',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Location: 'Music App Navigation',
				Status: getDownloadCentreAnalyticsStatus(
					useDownloadCentreStore.getState().messages
				),
			},
		})
	}

	return {
		handleAddToTeamButtonClick,
		handleCartButtonClick,
		handleServiceMenuButtonClick,
		handleAccountMenuButtonClick,
		handleDownloadCentreButtonPointerDown,
		handleDownloadCentreButtonKeyDown,
		handleDownloadCentreButtonClick,
		showAddToTeamMenu,
		showServiceMenu,
		showCNServiceMenu,
		itemsCount,
	}
}
